import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Avatar from './Avatar'
import TimeZoneSelector from './TimeZoneSelector'
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

export default function Account() {
  const { t } = useTranslation()
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(false)

  // Global error state for general errors (fetch/update)
  const [globalError, setGlobalError] = useState('')

  // Field states
  const [avatarUrl, setAvatarUrl] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  // Note: nationality now stores the country's id (a number)
  const [nationality, setNationality] = useState(null)
  const [employmentStatus, setEmploymentStatus] = useState('self_employed')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [address, setAddress] = useState('')
  const [bsn, setBsn] = useState('')
  const [timezoneId, setTimezoneId] = useState(null) // Now stores the timezone ID from the database

  // Employment details
  const [employerName, setEmployerName] = useState('')
  const [employerContact, setEmployerContact] = useState('')
  const [kvkNumber, setKvkNumber] = useState('')
  const [vatNumber, setVatNumber] = useState('')
  const [guarantorName, setGuarantorName] = useState('')
  const [guarantorContact, setGuarantorContact] = useState('')

  // List of countries for the nationality selector
  const [countries, setCountries] = useState([])

  // Validation errors for individual fields
  const [errors, setErrors] = useState({})

  // Fetch session on mount
  useEffect(() => {
    async function fetchSession() {
      try {
        const { data, error } = await supabase.auth.getSession()
        if (error) {
          setGlobalError(error.message)
        } else {
          setSession(data.session)
        }
      } catch (err) {
        setGlobalError(err.message)
      }
    }
    fetchSession()
  }, [])

  // Fetch countries for the nationality selector
  useEffect(() => {
    async function fetchCountries() {
      try {
        const { data, error } = await supabase.from('platform_countries').select('id, name')
        if (error) {
          setGlobalError(t('user.errorFetchCountries') + ': ' + error.message)
        } else {
          setCountries(data)
        }
      } catch (err) {
        setGlobalError(err.message)
      }
    }
    fetchCountries()
  }, [t])

  // Fetch the user profile once the session is available
  useEffect(() => {
    if (!session) return
    async function getProfile() {
      try {
        setLoading(true)
        const { user } = session
        const { data, error } = await supabase
          .from('profiles')
          .select(
            'first_name, last_name, phone_number, avatar_url, employment_status, date_of_birth, nationality, address, bsn, employer_name, employer_contact, kvk_number, vat_number, guarantor_name, guarantor_contact, timezone_id'
          )
          .eq('id', user.id)
          .single()

        if (error) throw error

        setFirstName(data.first_name || '')
        setLastName(data.last_name || '')
        setPhoneNumber(data.phone_number || '')
        setAvatarUrl(data.avatar_url || '')
        setEmploymentStatus(data.employment_status || 'student')
        setDateOfBirth(data.date_of_birth ? data.date_of_birth.split('T')[0] : '')
        // Here we expect data.nationality to be a number (or null)
        setNationality(data.nationality || null)
        setAddress(data.address || '')
        setBsn(data.bsn || '')
        setEmployerName(data.employer_name || '')
        setEmployerContact(data.employer_contact || '')
        setKvkNumber(data.kvk_number || '')
        setVatNumber(data.vat_number || '')
        setGuarantorName(data.guarantor_name || '')
        setGuarantorContact(data.guarantor_contact || '')
        setTimezoneId(data.timezone_id || null) // Set timezone ID
      } catch (error) {
        setGlobalError(t('user.errorFetchProfile') + ': ' + error.message)
      } finally {
        setLoading(false)
      }
    }
    getProfile()
  }, [session, t])

  // Validate required fields; mark missing ones with error messages
  function validateForm() {
    const newErrors = {}
    if (!firstName) newErrors.firstName = t('user.requiredField')
    if (!lastName) newErrors.lastName = t('user.requiredField')
    if (!phoneNumber) newErrors.phoneNumber = t('user.requiredField')
    if (!nationality) newErrors.nationality = t('user.requiredField')
    if (!timezoneId) newErrors.timezoneId = t('user.requiredField')

    if (employmentStatus === 'employed') {
      if (!employerName) newErrors.employerName = t('user.requiredField')
      if (!employerContact) newErrors.employerContact = t('user.requiredField')
    }
    if (employmentStatus === 'self_employed') {
      if (!kvkNumber) newErrors.kvkNumber = t('user.requiredField')
      if (!vatNumber) newErrors.vatNumber = t('user.requiredField')
    }
    if (employmentStatus === 'student') {
      if (!guarantorName) newErrors.guarantorName = t('user.requiredField')
      if (!guarantorContact) newErrors.guarantorContact = t('user.requiredField')
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Update the profile after validating required fields
  async function updateProfile(newAvatarUrl) {
    setGlobalError('') // clear any previous errors

    if (!validateForm()) {
      setGlobalError(t('user.fillRequiredFields'))
      return
    }

    try {
      setLoading(true)
      const { user } = session

      const profileUpdates = {
        id: user.id,
        avatar_url: newAvatarUrl !== undefined ? newAvatarUrl : avatarUrl,
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNumber,
        employment_status: employmentStatus,
        date_of_birth: dateOfBirth || null,
        // Send the nationality as a number (bigint)
        nationality: nationality,
        address,
        bsn,
        // Set employment-related fields conditionally
        employer_name: employmentStatus === 'employed' ? employerName : null,
        employer_contact: employmentStatus === 'employed' ? employerContact : null,
        kvk_number: employmentStatus === 'self_employed' ? kvkNumber : null,
        vat_number: employmentStatus === 'self_employed' ? vatNumber : null,
        guarantor_name: employmentStatus === 'student' ? guarantorName : null,
        guarantor_contact: employmentStatus === 'student' ? guarantorContact : null,
        timezone_id: timezoneId, // Save the timezone ID instead of code
        updated_at: new Date(),
      }

      const { error } = await supabase.from('profiles').upsert(profileUpdates)
      if (error) throw error
      alert(t('user.profileUpdated'))
    } catch (error) {
      setGlobalError(error.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleAvatarUpload(filePath) {
    setAvatarUrl(filePath)
    await updateProfile(filePath)
  }

  const handleTimezoneChange = (event) => {
    setTimezoneId(event.target.value)
  }

  if (!session) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Paper sx={{ p: 2, maxWidth: 600, margin: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        {t('user.myAccount')}
      </Typography>

      {globalError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {globalError}
        </Alert>
      )}

      <Box display="flex" justifyContent="center" mb={2}>
        <Avatar url={avatarUrl} size={120} onUpload={handleAvatarUpload} />
      </Box>

      <TextField
        fullWidth
        margin="normal"
        label={t('user.email')}
        value={session.user.email}
        disabled
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('user.firstName')}
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        error={!!errors.firstName}
        helperText={errors.firstName}
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('user.lastName')}
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        error={!!errors.lastName}
        helperText={errors.lastName}
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('user.phoneNumber')}
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value)}
        error={!!errors.phoneNumber}
        helperText={errors.phoneNumber}
      />

      {/* Nationality Selector */}
      <FormControl fullWidth margin="normal" error={!!errors.nationality}>
        <InputLabel id="nationality-select-label">{t('user.nationality')}</InputLabel>
        <Select
          labelId="nationality-select-label"
          value={nationality || ''}
          onChange={(e) => setNationality(Number(e.target.value))}
          label={t('user.nationality')}
        >
          {countries.map((country) => (
            <MenuItem key={country.id} value={country.id}>
              {country.name}
            </MenuItem>
          ))}
        </Select>
        {errors.nationality && (
          <Typography variant="caption" color="error">
            {errors.nationality}
          </Typography>
        )}
      </FormControl>

      {/* Timezone Selector - UPDATED */}
      <Box mt={3} mb={2}>
        <Divider sx={{ mb: 3 }} />
        <TimeZoneSelector value={timezoneId} onChange={handleTimezoneChange} disabled={loading} />
        {errors.timezoneId && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {errors.timezoneId}
          </Typography>
        )}
      </Box>
      <Divider sx={{ mt: 3, mb: 3 }} />

      <FormControl fullWidth margin="normal">
        <InputLabel id="employment-status-select-label">{t('user.employmentStatus')}</InputLabel>
        <Select
          labelId="employment-status-select-label"
          value={employmentStatus}
          onChange={(e) => setEmploymentStatus(e.target.value)}
          label={t('user.employmentStatus')}
        >
          <MenuItem value="student">{t('user.student')}</MenuItem>
          <MenuItem value="employed">{t('user.employed')}</MenuItem>
          <MenuItem value="self_employed">{t('user.selfEmployed')}</MenuItem>
        </Select>
      </FormControl>

      {/* Conditional Employment Details */}
      {employmentStatus === 'employed' && (
        <>
          <TextField
            fullWidth
            margin="normal"
            label={t('user.employerName')}
            value={employerName}
            onChange={(e) => setEmployerName(e.target.value)}
            error={!!errors.employerName}
            helperText={errors.employerName}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('user.employerContact')}
            value={employerContact}
            onChange={(e) => setEmployerContact(e.target.value)}
            error={!!errors.employerContact}
            helperText={errors.employerContact}
          />
        </>
      )}

      {employmentStatus === 'self_employed' && (
        <>
          <TextField
            fullWidth
            margin="normal"
            label={t('user.kvkNumber')}
            value={kvkNumber}
            onChange={(e) => setKvkNumber(e.target.value)}
            error={!!errors.kvkNumber}
            helperText={errors.kvkNumber}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('user.vatNumber')}
            value={vatNumber}
            onChange={(e) => setVatNumber(e.target.value)}
            error={!!errors.vatNumber}
            helperText={errors.vatNumber}
          />
        </>
      )}

      {employmentStatus === 'student' && (
        <>
          <TextField
            fullWidth
            margin="normal"
            label={t('user.guarantorName')}
            value={guarantorName}
            onChange={(e) => setGuarantorName(e.target.value)}
            error={!!errors.guarantorName}
            helperText={errors.guarantorName}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('user.guarantorContact')}
            value={guarantorContact}
            onChange={(e) => setGuarantorContact(e.target.value)}
            error={!!errors.guarantorContact}
            helperText={errors.guarantorContact}
          />
        </>
      )}

      {/* Button Container: Update Profile and Sign Out aligned */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => updateProfile()}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : t('user.updateProfile')}
        </Button>
        <Button variant="outlined" color="error" onClick={() => supabase.auth.signOut()}>
          {t('user.signOut')}
        </Button>
      </Box>
    </Paper>
  )
}
